import React from 'react';
import { Info, Landmark, DollarSign, Shield, Calendar, Check, AlertTriangle, Calculator } from 'lucide-react';

/**
 * Компонент с информацией о рефинансировании кредитов и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о рефинансировании кредитов
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Что такое рефинансирование кредита?
        </h3>
        <p>
          <strong>Рефинансирование кредита</strong> — это финансовая операция, при которой заёмщик оформляет новый кредит для погашения существующего. Обычно рефинансирование проводится с целью уменьшить процентную ставку, снизить ежемесячный платеж, изменить срок кредита или получить дополнительные денежные средства.
        </p>

        <h3 className="flex items-center gap-2">
          <Check className="w-5 h-5 text-green-500" />
          Когда выгодно рефинансирование?
        </h3>
        <ul>
          <li><strong>Снижение ставки:</strong> Если процентные ставки на рынке снизились или улучшился ваш кредитный рейтинг.</li>
          <li><strong>Уменьшение ежемесячного платежа:</strong> Актуально при временных финансовых трудностях или желании снизить кредитную нагрузку.</li>
          <li><strong>Сокращение срока кредита:</strong> Позволяет быстрее закрыть долг и сэкономить на процентах.</li>
          <li><strong>Объединение нескольких кредитов:</strong> Упрощает управление долгами и часто снижает общую нагрузку.</li>
          <li><strong>Получение дополнительных средств:</strong> Возможность взять сумму больше остатка по текущему кредиту.</li>
          <li><strong>Изменение условий кредита:</strong> Переход от плавающей ставки к фиксированной или наоборот.</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Важно знать!</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Рефинансирование выгодно, когда экономия на процентах превышает затраты на оформление нового кредита. В среднем, рефинансирование целесообразно, если новая ставка ниже текущей на 1-2 процентных пункта.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Landmark className="w-5 h-5 text-indigo-500" />
          Процесс рефинансирования в России
        </h3>
        <ol>
          <li><strong>Оценка целесообразности:</strong> Анализ текущего кредита и рыночных предложений.</li>
          <li><strong>Выбор банка:</strong> Сравнение предложений по рефинансированию от разных банков.</li>
          <li><strong>Сбор документов:</strong> Подготовка паспорта, справки о доходах, документов по текущему кредиту.</li>
          <li><strong>Подача заявки:</strong> Заполнение заявления в выбранном банке.</li>
          <li><strong>Рассмотрение заявки:</strong> Банк проверяет кредитную историю и платежеспособность (обычно 1-5 дней).</li>
          <li><strong>Одобрение и оформление:</strong> Подписание договора с новым банком.</li>
          <li><strong>Погашение старого кредита:</strong> Новый банк переводит средства для закрытия предыдущего кредита.</li>
          <li><strong>Обслуживание нового кредита:</strong> Выплаты по новому графику.</li>
        </ol>

        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-500" />
          Затраты на рефинансирование
        </h3>
        <p>
          При расчете выгоды от рефинансирования необходимо учитывать следующие возможные расходы:
        </p>
        <ul>
          <li><strong>Комиссия за выдачу нового кредита:</strong> От 0% до 2% от суммы.</li>
          <li><strong>Страхование:</strong> Многие банки требуют страхование жизни и здоровья заемщика.</li>
          <li><strong>Оценка залога:</strong> При рефинансировании ипотеки (5000-15000 рублей).</li>
          <li><strong>Комиссия за досрочное погашение:</strong> Если в текущем кредитном договоре предусмотрены штрафы.</li>
          <li><strong>Нотариальные и регистрационные сборы:</strong> Для ипотечных кредитов.</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lgborder border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при оформлении рефинансирования
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Внимательно читайте договор, особенно разделы о комиссиях и страховании</li>
            <li>Уточните полную стоимость кредита (ПСК) с учетом всех дополнительных платежей</li>
            <li>Проверьте условия досрочного погашения нового кредита</li>
            <li>Узнайте о возможности снижения ставки при подключении дополнительных услуг</li>
            <li>Не забудьте получить справку о полном погашении предыдущего кредита</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-indigo-500" />
          Особенности рефинансирования разных типов кредитов
        </h3>
        
        <h4>Ипотека</h4>
        <p>
          Рефинансирование ипотеки — наиболее сложный, но и наиболее выгодный вид рефинансирования из-за больших сумм и длительных сроков.
        </p>
        <ul>
          <li>В России действуют специальные программы господдержки: семейная ипотека, льготная ипотека, дальневосточная ипотека</li>
          <li>Требуется оценка залогового имущества (квартиры или дома)</li>
          <li>Обязательно нотариальное согласие супруга(-и) при наличии</li>
          <li>Понадобится перерегистрация залога в Росреестре</li>
          <li>Выгодно при снижении ставки от 0,5% и выше</li>
        </ul>
        
        <h4>Потребительские кредиты</h4>
        <p>
          Процесс рефинансирования происходит быстрее и проще, чем при ипотеке.
        </p>
        <ul>
          <li>Минимальный пакет документов: паспорт, справка о доходах, выписка по текущему кредиту</li>
          <li>Возможность объединить несколько мелких кредитов в один</li>
          <li>Выгодно, если новая ставка ниже текущей на 2-3% и более</li>
          <li>Быстрое оформление — в среднем 1-3 дня</li>
        </ul>
        
        <h4>Автокредиты</h4>
        <p>
          При рефинансировании автокредита автомобиль остается в залоге, но меняется залогодержатель.
        </p>
        <ul>
          <li>Потребуется оценка рыночной стоимости автомобиля</li>
          <li>Возможна необходимость перерегистрации залога в ГИБДД</li>
          <li>Банки предъявляют требования к возрасту и состоянию автомобиля</li>
          <li>Многие банки не рефинансируют автокредиты старше 3-5 лет</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-indigo-500" />
          Кто может рефинансировать кредит в России
        </h3>
        <p>
          Большинство российских банков предъявляют к заемщикам следующие требования:
        </p>
        <ul>
          <li>Гражданство РФ и возраст от 21 до 65-70 лет</li>
          <li>Стабильный доход, подтвержденный справкой 2-НДФЛ или справкой по форме банка</li>
          <li>Стаж на текущем месте работы не менее 3-6 месяцев</li>
          <li>Хорошая кредитная история</li>
          <li>По рефинансируемому кредиту должно быть сделано 3-6 платежей без просрочек</li>
          <li>Остаток срока по текущему кредиту — не менее 3-6 месяцев</li>
        </ul>

        <h3>Частые вопросы о рефинансировании</h3>
        
        <h4>Можно ли рефинансировать кредит в том же банке?</h4>
        <p>
          Да, это называется внутренним рефинансированием. Однако не все банки предлагают такую услугу. Преимущество — упрощенный процесс оформления, недостаток — условия могут быть менее выгодными, чем при переходе в другой банк.
        </p>
        
        <h4>Как рефинансирование влияет на кредитную историю?</h4>
        <p>
          При правильном оформлении рефинансирование не ухудшает кредитную историю. В ней будет отображено, что предыдущий кредит успешно закрыт, и открыт новый. Главное — не допустить просрочки в переходный период.
        </p>
        
        <h4>Сколько раз можно рефинансировать кредит?</h4>
        <p>
          Формально ограничений нет, но банки с осторожностью относятся к заемщикам, которые часто рефинансируют кредиты. Рекомендуется делать это не чаще одного раза в 1-2 года и только при значительной выгоде.
        </p>
        
        <h4>Можно ли взять сумму больше остатка по кредиту?</h4>
        <p>
          Да, многие банки предлагают рефинансирование с дополнительной суммой. Это удобно, если появились новые финансовые потребности, но следует помнить, что увеличение суммы кредита повышает долговую нагрузку.
        </p>

        <p className="font-semibold mt-6">
          Наш калькулятор рефинансирования поможет вам оценить потенциальную выгоду и принять взвешенное решение о целесообразности рефинансирования вашего кредита!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;